import { useSelector } from "react-redux";

/**
 * Codes de permission utilisés pour conditionner les ACTIONS de l'interface
 * (boutons, éléments de menu déroulant). Ils doivent rester identiques à ceux
 * du backend (générés automatiquement par seeds/02_permissions.js : chaque
 * module x chaque action standard, cf. MODULES/STANDARD_ACTIONS ci-dessous,
 * plus quelques permissions spécifiques métier).
 */

// Modules "standards" : mêmes noms que dans seeds/02_permissions.js.
const MODULES = [
  "ROLE", "PERMISSION", "USER", "USER_PROFILE",
  "FACULTY", "DEPARTMENT", "ACADEMIC_YEAR", "SEMESTER", "LEVEL", "CLASS",
  "STUDENT_GROUP", "SUBJECT",
  "TEACHER_PROFILE", "STUDENT_PROFILE", "STAFF_PROFILE", "ENROLLMENT",
  "QUESTION_CATEGORY", "QUESTION_DIFFICULTY", "QUESTION_TYPE", "QUESTION", "QUESTION_BANK",
  "EXAM", "EXAM_SESSION", "EXAM_RESULT", "STUDENT_ATTEMPT",
  "ROOM", "TABLET", "EXAM_INCIDENT",
  "CLINICAL_CASE", "REFERENCE_VALUE", "GRADE_CORRECTION_REQUEST",
  "REPORT", "STATISTIC",
  "NOTIFICATION", "SETTING", "AUDIT_LOG",
];

const STANDARD_ACTIONS = [
  "LIST", "READ", "CREATE", "UPDATE", "DELETE", "RESTORE", "IMPORT", "EXPORT", "PRINT", "SHARE",
];

export const PERMISSIONS = {};
MODULES.forEach((module) => {
  STANDARD_ACTIONS.forEach((action) => {
    const code = `${module}_${action}`;
    PERMISSIONS[code] = code;
  });
});

// Permissions spécifiques métier (hors CRUD standard) - cf. seeds/02_permissions.js
// et le circuit de validation dans workflowPermissions.js côté backend.
Object.assign(PERMISSIONS, {
  ROLE_SET_PERMISSIONS: "ROLE_SET_PERMISSIONS",

  USER_ASSIGN_ROLE: "USER_ASSIGN_ROLE",
  USER_REMOVE_ROLE: "USER_REMOVE_ROLE",
  USER_SEND_CREDENTIALS: "USER_SEND_CREDENTIALS",

  ACADEMIC_YEAR_SET_CURRENT: "ACADEMIC_YEAR_SET_CURRENT",

  EXAM_PUBLISH: "EXAM_PUBLISH",
  EXAM_UNPUBLISH: "EXAM_UNPUBLISH",
  EXAM_ARCHIVE: "EXAM_ARCHIVE",
  EXAM_VALIDATE: "EXAM_VALIDATE",
  EXAM_NEUTRALIZE_QUESTION: "EXAM_NEUTRALIZE_QUESTION",

  EXAM_SESSION_OPEN: "EXAM_SESSION_OPEN",
  EXAM_SESSION_CLOSE: "EXAM_SESSION_CLOSE",

  EXAM_RESULT_PUBLISH: "EXAM_RESULT_PUBLISH",
  EXAM_RESULT_RECOMPUTE: "EXAM_RESULT_RECOMPUTE",

  STUDENT_ATTEMPT_START: "STUDENT_ATTEMPT_START",
  STUDENT_ATTEMPT_SUBMIT: "STUDENT_ATTEMPT_SUBMIT",
  STUDENT_ATTEMPT_CANCEL: "STUDENT_ATTEMPT_CANCEL",

  GRADE_CORRECTION_REQUEST_APPROVE: "GRADE_CORRECTION_REQUEST_APPROVE",
  GRADE_CORRECTION_REQUEST_REJECT: "GRADE_CORRECTION_REQUEST_REJECT",

  NOTIFICATION_EMAIL_LOGS: "NOTIFICATION_EMAIL_LOGS",

  SETTING_RESTORE_DEFAULTS: "SETTING_RESTORE_DEFAULTS",
});

/**
 * Même règle que le backend : SUPER_ADMIN passe toujours, les autres comptes
 * doivent porter la permission (ou l'une des permissions si un tableau est
 * passé).
 */
export function userCan(user, permission) {
  if (!user) return false;
  if ((user.roles || []).includes("SUPER_ADMIN")) return true;
  const owned = user.permissions || [];
  const wanted = Array.isArray(permission) ? permission : [permission];
  return wanted.some((code) => owned.includes(code));
}

/**
 * Hook : `const can = usePermissions(); can("EXAM_PUBLISH")`.
 */
export function usePermissions() {
  const user = useSelector((state) => state.auth.auth?.user);
  return (permission) => userCan(user, permission);
}
